import React from 'react';
import {
Box,
Paper,
Typography,
TableContainer,
Table,
TableHead,
TableBody,
TableRow,
TableCell
} from '@mui/material';
import {
BarChart as BarChartIcon
} from '@mui/icons-material';
import {
ResponsiveContainer,
BarChart,
Bar,
LabelList,
XAxis,
YAxis,
Tooltip,
Legend,
DefaultLegendContent
} from 'recharts';
import type { LabelProps, LegendPayload } from 'recharts';
import { DashboardData } from '../../types';
import {
    getDepartmentContentData,
    getOccupationContentData,
    getTimingContentData,
    generateContentColors,
    orderedResultTypes,
    COLORS
} from './utils/dataProcessing';
import {
  CategoryAxisTick,
  createTooltipItemSorter,
  useCategoryAxisLayout,
} from './utils/chartUtils';

interface ComplianceRateTabProps {
data: DashboardData;
}

export const ComplianceBarLabel = ({ value, viewBox }: LabelProps) => {
const { x, y, width, height } = (viewBox || {}) as {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
};
const percent = typeof value === 'number'
  ? value
  : typeof value === 'string'
    ? Number(value)
    : Number.NaN;

if (
  typeof x !== 'number' ||
  typeof y !== 'number' ||
  typeof width !== 'number' ||
  typeof height !== 'number' ||
  !Number.isFinite(percent) ||
  width < 40 ||
  height < 20 ||
  percent < 5
) return null;

return (
  <text
    x={x + width / 2}
    y={y + height / 2}
    textAnchor="middle"
    dominantBaseline="middle"
    fill="#fff"
    fontSize={12}
    fontWeight="bold"
  >
    {Math.round(percent)}%
  </text>
);
};

const complianceTooltipItemSorter = createTooltipItemSorter(orderedResultTypes);

const ComplianceRateTab: React.FC<ComplianceRateTabProps> = ({ data }) => {
// データの加工
const departmentContentData = getDepartmentContentData(data);
const occupationContentData = getOccupationContentData(data);
const timingContentData = getTimingContentData(data);
const contentColorMap = generateContentColors(data);
const departmentAxisLayout = useCategoryAxisLayout(
  departmentContentData.map((department) => department.name),
);

const legendPayload: LegendPayload[] = orderedResultTypes.map((content, index) => ({
  value: content,
  type: 'square',
  color: contentColorMap[content] || COLORS[index % COLORS.length],
}));

const renderLegend = (props: any) => (
  <DefaultLegendContent {...props} payload={legendPayload} />
);

return (
    <Box sx={{ width: '100%' }}>
    {/* 部署ごとの結果 */}
    <Box sx={{ mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <BarChartIcon sx={{ mr: 1 }} /> 部署
        </Typography>
        {/* グラフの高さを部署数に応じて自動調整 - 部署1つあたり最低40pxを確保 */}
        <Box sx={{ height: Math.max(400, departmentContentData.length * 40) }}>
            <ResponsiveContainer width="100%" height="100%">
            <BarChart
                layout="vertical"
                data={departmentContentData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
                <XAxis
                type="number"
                domain={[0, 100]}
                tickFormatter={(value) => `${Math.round(value)}%`} // 小数点以下を四捨五入
                ticks={[0, 20, 40, 60, 80, 100]} // 表示する目盛り値
                />
                <YAxis
                type="category"
                dataKey="name"
                width={departmentAxisLayout.width}
                tick={(
                  <CategoryAxisTick
                    maxCharacters={departmentAxisLayout.maxCharacters}
                  />
                )}
                />
                <Tooltip
                itemSorter={complianceTooltipItemSorter}
                formatter={(value, name, props) => {
                    // nameが文字列かどうか確認
                    const itemName = typeof name === 'string' ? name : String(name);
                    // Percentで終わる場合のみreplaceを行う
                    const contentKey = itemName.endsWith('Percent')
                    ? itemName.substring(0, itemName.length - 7)
                    : itemName;

                    // propsからpayloadを取得して元の件数データにアクセス
                    const count = props.payload[contentKey];

                    // valueが数値かどうか確認
                    const percentage = typeof value === 'number'
                    ? `${value.toFixed(1)}%`
                    : `${value}%`;

                    return [`${count}件 (${percentage})`, contentKey];
                }}
                contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '1px solid #ccc' }}
                />
                <Legend content={renderLegend} />
                {/* 指定された順序で内容タイプを表示 */}
                {orderedResultTypes.map((content, index) => (
                <Bar
                    key={content}
                    dataKey={`${content}Percent`}
                    name={content}
                    stackId="a"
                    fill={contentColorMap[content] || COLORS[index % COLORS.length]}
                    isAnimationActive={false}
                >
                    <LabelList
                    dataKey={`${content}Percent`}
                    content={ComplianceBarLabel}
                    />
                </Bar>
                ))}
            </BarChart>
            </ResponsiveContainer>
        </Box>

        {/* 部署ごとの結果の表 - 指定された順序で列を表示 */}
        <TableContainer sx={{ maxHeight: 300 }}>
            <Table stickyHeader size="small">
            <TableHead>
                <TableRow>
                <TableCell>部署</TableCell>
                {orderedResultTypes.map(content => (
                    <TableCell key={content} align="center">{content}</TableCell>
                ))}
                <TableCell align="center">合計</TableCell>
                {/* 遵守率列を追加 */}
                <TableCell align="center" sx={{ backgroundColor: '#f5f5f5' }}>遵守率</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {departmentContentData.map((row, index) => {
                // 合計を計算
                const total = orderedResultTypes.reduce((sum, content) => sum + (row[content] || 0), 0);

                // 遵守率を計算（アルコール + 石鹸と流水）/ 合計 * 100
                const complianceCount = (row["アルコールで実施した"] || 0) + (row["石鹸と流水で実施した"] || 0);
                const complianceRate = total > 0 ? (complianceCount / total * 100).toFixed(1) : "0.0";

                return (
                    <TableRow key={index}>
                    <TableCell>{row.name}</TableCell>
                    {orderedResultTypes.map(content => (
                        <TableCell key={content} align="center">{row[content]}</TableCell>
                    ))}
                    <TableCell align="center">{total}</TableCell>
                    {/* 遵守率セルを追加 */}
                    <TableCell align="center" sx={{
                        backgroundColor: '#f5f5f5',
                        fontWeight: 'bold',
                        color: parseFloat(complianceRate) >= 80 ? 'green' :
                            parseFloat(complianceRate) >= 50 ? 'black' : 'red'
                    }}>
                        {complianceRate}%
                    </TableCell>
                    </TableRow>
                );
                })}
            </TableBody>
            </Table>
        </TableContainer>
        </Paper>
    </Box>

    {/* 職種ごとの結果 */}
    <Box sx={{ mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <BarChartIcon sx={{ mr: 1 }} /> 職種
        </Typography>
        {/* グラフの高さを職種数に応じて自動調整 - 職種1つあたり最低40pxを確保 */}
        <Box sx={{ height: Math.max(400, occupationContentData.length * 40) }}>
            <ResponsiveContainer width="100%" height="100%">
            <BarChart
                layout="vertical"
                data={occupationContentData}
                margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
            >
                <XAxis
                type="number"
                domain={[0, 100]}
                tickFormatter={(value) => `${Math.round(value)}%`} // 小数点以下を四捨五入
                ticks={[0, 20, 40, 60, 80, 100]} // 表示する目盛り値
                />
                <YAxis type="category" dataKey="name" width={80} />
                <Tooltip
                itemSorter={complianceTooltipItemSorter}
                formatter={(value, name, props) => {
                    // nameが文字列かどうか確認
                    const itemName = typeof name === 'string' ? name : String(name);
                    // Percentで終わる場合のみreplaceを行う
                    const contentKey = itemName.endsWith('Percent')
                    ? itemName.substring(0, itemName.length - 7)
                    : itemName;

                    // propsからpayloadを取得して元の件数データにアクセス
                    const count = props.payload[contentKey];

                    // valueが数値かどうか確認
                    const percentage = typeof value === 'number'
                    ? `${value.toFixed(1)}%`
                    : `${value}%`;

                    return [`${count}件 (${percentage})`, contentKey];
                }}
                contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '1px solid #ccc' }}
                />
                <Legend content={renderLegend} />
                {/* 指定された順序で内容タイプを表示 */}
                {orderedResultTypes.map((content, index) => (
                <Bar
                    key={content}
                    dataKey={`${content}Percent`}
                    name={content}
                    stackId="a"
                    fill={contentColorMap[content] || COLORS[index % COLORS.length]}
                    isAnimationActive={false}
                >
                    <LabelList
                    dataKey={`${content}Percent`}
                    content={ComplianceBarLabel}
                    />
                </Bar>
                ))}
            </BarChart>
            </ResponsiveContainer>
        </Box>

        {/* 職種ごとの結果の表 - 指定された順序で列を表示 */}
        <TableContainer sx={{ maxHeight: 300 }}>
            <Table stickyHeader size="small">
            <TableHead>
                <TableRow>
                <TableCell>職種</TableCell>
                {orderedResultTypes.map(content => (
                    <TableCell key={content} align="center">{content}</TableCell>
                ))}
                <TableCell align="center">合計</TableCell>
                {/* 遵守率列を追加 */}
                <TableCell align="center" sx={{ backgroundColor: '#f5f5f5' }}>遵守率</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {occupationContentData.map((row, index) => {
                // 合計を計算
                const total = orderedResultTypes.reduce((sum, content) => sum + (row[content] || 0), 0);

                // 遵守率を計算（アルコール + 石鹸と流水）/ 合計 * 100
                const complianceCount = (row["アルコールで実施した"] || 0) + (row["石鹸と流水で実施した"] || 0);
                const complianceRate = total > 0 ? (complianceCount / total * 100).toFixed(1) : "0.0";

                return (
                    <TableRow key={index}>
                    <TableCell>{row.name}</TableCell>
                    {orderedResultTypes.map(content => (
                        <TableCell key={content} align="center">{row[content]}</TableCell>
                    ))}
                    <TableCell align="center">{total}</TableCell>
                    {/* 遵守率セルを追加 */}
                    <TableCell align="center" sx={{
                        backgroundColor: '#f5f5f5',
                        fontWeight: 'bold',
                        color: parseFloat(complianceRate) >= 80 ? 'green' :
                            parseFloat(complianceRate) >= 50 ? 'black' : 'red'
                    }}>
                        {complianceRate}%
                    </TableCell>
                    </TableRow>
                );
                })}
            </TableBody>
            </Table>
        </TableContainer>
        </Paper>
    </Box>

    {/* タイミングごとの結果 */}
    <Box sx={{ mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <BarChartIcon sx={{ mr: 1 }} /> 観察タイミング
        </Typography>
        {/* グラフの高さを部署ごと・職種ごとのグラフと同じ計算方法で調整 */}
        <Box sx={{ height: Math.max(300, timingContentData.length * 40) }}>
            <ResponsiveContainer width="100%" height="100%">
            <BarChart
                layout="vertical"
                data={timingContentData}
                margin={{ top: 20, right: 30, left: 140, bottom: 5 }}
            >
                <XAxis
                type="number"
                domain={[0, 100]}
                tickFormatter={(value) => `${Math.round(value)}%`} // 小数点以下を四捨五入
                ticks={[0, 20, 40, 60, 80, 100]} // 表示する目盛り値
                />
                <YAxis type="category" dataKey="name" width={120} />
                <Tooltip
                itemSorter={complianceTooltipItemSorter}
                formatter={(value, name, props) => {
                    // nameが文字列かどうか確認
                    const itemName = typeof name === 'string' ? name : String(name);
                    // Percentで終わる場合のみreplaceを行う
                    const contentKey = itemName.endsWith('Percent')
                    ? itemName.substring(0, itemName.length - 7)
                    : itemName;

                    // propsからpayloadを取得して元の件数データにアクセス
                    const count = props.payload[contentKey + 'Count'] || props.payload[contentKey];

                    // valueが数値かどうか確認
                    const percentage = typeof value === 'number'
                    ? `${value.toFixed(1)}%`
                    : `${value}%`;

                    return [`${count}件 (${percentage})`, contentKey];
                }}
                contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '1px solid #ccc' }}
                />
                <Legend content={renderLegend} />
                {/* 指定された順序で内容タイプを表示 */}
                {orderedResultTypes.map((content, index) => (
                <Bar
                    key={content}
                    dataKey={`${content}Percent`}
                    name={content}
                    stackId="a"
                    fill={contentColorMap[content] || COLORS[index % COLORS.length]}
                    isAnimationActive={false}
                >
                    <LabelList
                    dataKey={`${content}Percent`}
                    content={ComplianceBarLabel}
                    />
                </Bar>
                ))}
            </BarChart>
            </ResponsiveContainer>
        </Box>

        {/* テーブル部分も同じ順序で表示するように修正 */}
        <TableContainer sx={{ maxHeight: 300 }}>
            <Table stickyHeader size="small">
            <TableHead>
                <TableRow>
                <TableCell>タイミング</TableCell>
                {orderedResultTypes.map(content => (
                    <TableCell key={content} align="center">{content}</TableCell>
                ))}
                <TableCell align="center">合計</TableCell>
                {/* 遵守率列を追加 */}
                <TableCell align="center" sx={{ backgroundColor: '#f5f5f5' }}>遵守率</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {timingContentData.map((row, index) => {
                // 合計を計算
                const total = orderedResultTypes.reduce((sum, content) => sum + (row[content] || 0), 0);

                // 遵守率を計算（アルコール + 石鹸と流水）/ 合計 * 100
                const complianceCount = (row["アルコールで実施した"] || 0) + (row["石鹸と流水で実施した"] || 0);
                const complianceRate = total > 0 ? (complianceCount / total * 100).toFixed(1) : "0.0";

                return (
                    <TableRow key={index}>
                    <TableCell>{row.name}</TableCell>
                    {orderedResultTypes.map(content => (
                        <TableCell key={content} align="center">{row[content] || 0}</TableCell>
                    ))}
                    <TableCell align="center">{total}</TableCell>
                    {/* 遵守率セルを追加 */}
                    <TableCell align="center" sx={{
                        backgroundColor: '#f5f5f5',
                        fontWeight: 'bold',
                        color: parseFloat(complianceRate) >= 80 ? 'green' :
                            parseFloat(complianceRate) >= 50 ? 'black' : 'red'
                    }}>
                        {complianceRate}%
                    </TableCell>
                    </TableRow>
                );
                })}
            </TableBody>
            </Table>
        </TableContainer>
        </Paper>
    </Box>
    </Box>
);
};

export default ComplianceRateTab;
